import { useCallback, useEffect, useRef } from 'react';

import {
  GetAccountDetailsResponse,
  MarginPosition,
  MarginPositionStatus,
  MarginTraderAccount,
} from '@swyftx/margin/build/types';

import env from '@shared/config';
import { TimesEnum } from '@shared/enums';
import { Big } from '@shared/safe-big';
import { UserStore } from '@shared/store';

import { useIntervalEffect } from '@react-hookz/web';
import { useQueryClient } from '@tanstack/react-query';
import { EventSource } from 'eventsource';
import { DateTime } from 'luxon';

import AccountWorker from './shared-worker?sharedworker';
import { useFetchMarginAccountDetailsCache } from './useFetchMarginAccountDetailsCache';
import { useSwyftxMarginStore } from '../../state';
import { mapPositions } from '../../utils';
import { useMarginPositionsCache } from '../positions/useMarginPositionsCache';
import { useMarginPositionsStore } from '../positions/useMarginPositionsStore';
import { useSwyftxMargin } from '../useSwyftxMargin';

type AccountStreamResponse =
  | {
      type: 'account';
      data: MarginTraderAccount;
      timestamp: number;
    }
  | {
      type: 'position';
      data: MarginPosition[];
      timestamp: number;
    };

const cancelledStatuses: MarginPositionStatus[] = [
  MarginPositionStatus.Cancelled,
  MarginPositionStatus.Closed,
  MarginPositionStatus.Reject,
];

const SOCKET_STALE_TIME = 10_000;

const useAccountWorker = () => {
  const accountWorker = useRef<SharedWorker>();
  const staleData = useRef<number>(DateTime.now().plus({ millisecond: SOCKET_STALE_TIME }).toMillis());
  const eventSource = useRef<EventSource>();
  const positionsCache = useRef<Map<string, MarginPosition>>(new Map());

  const { getUniqueIdentifier, userProfile, userAuth, isLoggedIn } = UserStore.useUserStore;
  const { invalidateCache } = useMarginPositionsCache();
  const { invalidateCache: invalidateAccountCache } = useFetchMarginAccountDetailsCache();
  const { traderAccountId } = useSwyftxMarginStore();
  const { swyftxMarginEnabled } = useSwyftxMargin();

  const queryClient = useQueryClient();

  const incrementStaleDataTime = () => {
    staleData.current = DateTime.now().plus({ millisecond: SOCKET_STALE_TIME }).toMillis();
  };

  useIntervalEffect(() => {
    if (!swyftxMarginEnabled) return;

    if (DateTime.now().toMillis() > staleData.current) {
      invalidateAccountCache();
      invalidateCache();
      incrementStaleDataTime();
    }

    if (accountWorker.current?.port) {
      accountWorker.current.port.postMessage({ type: 'ping' });
    }
  }, TimesEnum.SECOND);

  const updateAccountDetails = useCallback(
    (data: MarginTraderAccount) => {
      queryClient.setQueryData(
        ['margin-account-details', getUniqueIdentifier(), traderAccountId],
        (oldData?: GetAccountDetailsResponse) => {
          if (traderAccountId !== data.traderAccountId) return;

          if (Big(data.lastUpdate).lte(oldData?.account.lastUpdate || 0)) return;

          if (oldData) {
            return { account: { ...oldData.account, ...data } };
          } else {
            return { account: data };
          }
        },
      );
    },
    [getUniqueIdentifier, queryClient, traderAccountId],
  );

  const savePositionData = useCallback(
    (data: MarginPosition[]) => {
      const { positions: cachedPositions, updatePositions } = useMarginPositionsStore.getState();

      // Only track positions that exist in the cache that have now changed state. i.e closed, pending -> open etc
      const updatedPosition = data.filter((d) => {
        const cached = cachedPositions.get(d.positionId);

        // if the position doesn't exist in the cache and its not cancelled/closed, we should add it to the list
        if (!cached && !cancelledStatuses.includes(d.status)) {
          return true;
        }

        // if the status of the position is different to what is in the cache add to the list
        if (cached && cached.status !== d.status) {
          return true;
        }

        return false;
      });

      // If we have some positions that have changed states, we want to force a refresh of the table data
      if (updatedPosition.length > 0) {
        invalidateCache();
      }

      updatePositions(mapPositions(data.filter((d) => d.traderAccountId === traderAccountId)));
    },
    [invalidateCache, traderAccountId],
  );

  const sendConfig = useCallback(() => {
    if (!accountWorker.current || !window.SharedWorker) return;

    accountWorker.current.port.postMessage({
      type: 'config',
      data: JSON.stringify({
        TRADE_UPDATES_SERVICE_URL: env.TRADE_UPDATES_SERVICE_URL,
        TRADER_ACCOUNT_ID: getUniqueIdentifier(),
        USER_AUTH_TOKEN: UserStore.useUserStore.userAuth?.access_token,
      }),
    });
  }, [getUniqueIdentifier]);

  const initSharedWorker = useCallback(
    (traderId?: string) => {
      if (accountWorker.current !== undefined || !traderId) return;

      accountWorker.current = new AccountWorker({
        name: 'swyftx-account-worker',
      });

      accountWorker.current.port.onmessage = (e) => {
        if (e.data.type === 'pong') return;

        if (e.data.type === 'disconnect') {
          accountWorker.current = undefined;
          initSharedWorker();
          return;
        }

        const data = e.data.payload as AccountStreamResponse;

        if (!data) return;

        if (data.timestamp && DateTime.fromMillis(data.timestamp) < DateTime.now().minus({ seconds: 1 })) return;

        // We have some valid data from the shared worker. update the stale timer
        incrementStaleDataTime();

        if (data.type === 'account') {
          updateAccountDetails(data.data);
        } else if (data.type === 'position') {
          savePositionData(data.data);
        }
      };

      accountWorker.current.port.start();

      sendConfig();
    },
    [savePositionData, sendConfig, updateAccountDetails],
  );

  const initEventSource = useCallback(() => {
    const traderId = getUniqueIdentifier();

    if (eventSource.current !== undefined || !traderId) return;

    // Connect to the SSE endpoint
    eventSource.current = new EventSource(`${env.TRADE_UPDATES_SERVICE_URL}/stream/user?traderId=${traderId}`, {
      fetch: (input, init) =>
        fetch(input, {
          ...init,
          headers: {
            ...init?.headers,
            Authorization: `Bearer ${userAuth?.access_token}`,
          },
        }),
    });

    // Listen for messages from the server
    eventSource.current.onmessage = (event) => {
      const data = JSON.parse(event.data);

      // We have some valid data from the shared worker. update the stale timer
      incrementStaleDataTime();

      if (data?.type === 'account') {
        updateAccountDetails(data?.data);
      } else if (data?.type === 'position') {
        positionsCache.current.set(data.data.positionId, data.data);
      }
    };

    eventSource.current.onerror = () => {
      eventSource.current?.close();
      eventSource.current = undefined; // Allow reconnection
    };

    window.setInterval(() => {
      savePositionData(Array.from(positionsCache.current.values()));
    }, 1000);
  }, [getUniqueIdentifier, savePositionData, updateAccountDetails, userAuth?.access_token]);

  const handleBeforeUnload = () => {
    if (accountWorker.current?.port) {
      accountWorker.current.port.postMessage({ type: 'disconnect' });
    }

    if (eventSource.current) {
      eventSource.current.close();
    }
  };

  useEffect(() => {
    if (!isLoggedIn && accountWorker.current) {
      accountWorker.current.port.close();
      accountWorker.current = undefined;
    }
  }, [isLoggedIn]);

  useEffect(() => {
    try {
      if (!swyftxMarginEnabled || !userProfile) return;

      if (window.SharedWorker) {
        initSharedWorker(getUniqueIdentifier());
      } else {
        initEventSource();
      }

      window.addEventListener('beforeunload', handleBeforeUnload);
    } catch (e) {
      console.error(e);
    }

    return () => {
      accountWorker.current?.port.postMessage({
        type: 'disconnect',
      });

      accountWorker.current?.port.close();
      accountWorker.current = undefined;

      eventSource.current?.close();
      eventSource.current = undefined;

      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [initEventSource, initSharedWorker, userProfile, swyftxMarginEnabled, getUniqueIdentifier]);
};

export { useAccountWorker };
